/**
 * Q Drives — Firebase Web client.
 *
 * Lazy-initialised: the SDK is only contacted when first used, so users who
 * stay in dev-OTP mode never pay the network/storage cost.
 *
 * Activation: set REACT_APP_AUTH_MODE=firebase in /app/frontend/.env and rebuild.
 * Until then, PhoneLogin.jsx stays on the dev OTP path.
 */
import { initializeApp, getApps } from "firebase/app";
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyBcvoxjXRylP3_xlVQaQI_mZmzHOQnCo1o",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "q-drives.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "q-drives",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "q-drives.firebasestorage.app",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "409191744130",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:409191744130:web:90d4d62912f892aea9ea9b",
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "G-PZ8XG9Y332",
};

export const AUTH_MODE = process.env.REACT_APP_AUTH_MODE || "firebase"; // "dev" | "firebase"

let _app = null;
let _auth = null;
let _recaptcha = null;
let _confirmation = null;

function ensureApp() {
  if (_app) return _app;
  _app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
  _auth = getAuth(_app);
  // Default language for SMS body
  _auth.languageCode = "en";
  return _app;
}

function ensureRecaptcha() {
  if (_recaptcha) return _recaptcha;
  ensureApp();
  // Invisible reCAPTCHA so it doesn't break the login UI. Container DIV must
  // exist before this call — PhoneLogin renders it explicitly.
  _recaptcha = new RecaptchaVerifier(_auth, "qd-recaptcha", {
    size: "invisible",
    callback: () => {},
    "expired-callback": () => {},
  });
  return _recaptcha;
}

/**
 * Send SMS OTP via Firebase. Phone must be E.164 (e.g. +919876543210).
 * Throws on bad phone, network failure, or quota.
 */
export async function sendOtpFirebase(phone) {
  ensureApp();
  const verifier = ensureRecaptcha();
  _confirmation = await signInWithPhoneNumber(_auth, phone, verifier);
}

/**
 * Verify the SMS code. Returns `{ idToken, uid, phone }`.
 * Caller POSTs idToken to /api/auth/firebase to mint a Q Drives JWT.
 */
export async function verifyOtpFirebase(code) {
  if (!_confirmation) {
    throw new Error("Call sendOtpFirebase first");
  }
  const cred = await _confirmation.confirm(code);
  const user = cred.user;
  const idToken = await user.getIdToken(true);
  return { idToken, uid: user.uid, phone: user.phoneNumber || "" };
}

/**
 * Clear the cached confirmation + reCAPTCHA so the user can re-request OTP.
 */
export function resetFirebasePhoneAuth() {
  _confirmation = null;
  if (_recaptcha) {
    try { _recaptcha.clear(); } catch { /* ignore */ }
    _recaptcha = null;
  }
}

/**
 * Sign out the Firebase session — call alongside our app logout.
 */
export async function signOutFirebase() {
  if (!_auth) return;
  try {
    const { signOut } = await import("firebase/auth");
    await signOut(_auth);
  } catch {
    /* best-effort */
  }
}

export function isFirebaseAuthMode() {
  return AUTH_MODE === "firebase";
}
